import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Camera } from "lucide-react";
import { CFM_SPECIALTIES, UF_LIST } from "@/data/cfmData";

const EMPTY = { name: "", crm: "", uf: "", specialty: "", repasse: "", avatar: "" };

export const DoctorFormModal = ({ open, onOpenChange, initial, onSave }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial, repasse: String(initial.repasse) } : EMPTY);
      setErrors({});
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [specOpen, setSpecOpen] = useState(false);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem válido.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem muito grande. Envie um arquivo de até 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("avatar", reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Informe o nome completo";
    if (!form.crm.trim()) e.crm = "Informe o CRM";
    if (!form.uf) e.uf = "Selecione a UF";
    if (!form.specialty) e.specialty = "Selecione a especialidade";
    const r = Number(form.repasse);
    if (form.repasse === "" || isNaN(r) || r < 0 || r > 100)
      e.repasse = "Repasse entre 0 e 100";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    onSave({ ...form, repasse: Number(form.repasse) });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="doctor-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold text-slate-900">
            {initial ? "Editar Médico" : "Novo Médico"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Defina a especialidade CFM e a porcentagem de repasse individual do médico.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4">
            {form.avatar ? (
              <img
                data-testid="doctor-photo-preview"
                src={form.avatar}
                alt="preview"
                className="h-16 w-16 rounded-2xl object-cover ring-1 ring-slate-200"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Camera className="h-6 w-6" />
              </div>
            )}
            <div>
              <label className="inline-block cursor-pointer rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                {form.avatar ? "Trocar foto" : "Enviar foto"}
                <input
                  data-testid="doctor-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={onPhoto}
                  className="hidden"
                />
              </label>
              <p className="mt-1 text-xs text-slate-400">Foto de perfil (opcional)</p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">Nome Completo</Label>
            <Input
              data-testid="doctor-name-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ex: Dr. João Pereira"
              className="mt-1.5 border-slate-200 focus-visible:ring-blue-600"
            />
            {errors.name && <p className="mt-1 text-xs text-rose-600">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Número do CRM</Label>
              <Input
                data-testid="doctor-crm-input"
                value={form.crm}
                onChange={(e) => set("crm", e.target.value)}
                placeholder="Ex: 123456"
                className="mt-1.5 border-slate-200 focus-visible:ring-blue-600"
              />
              {errors.crm && <p className="mt-1 text-xs text-rose-600">{errors.crm}</p>}
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">UF do CRM</Label>
              <Select value={form.uf} onValueChange={(v) => set("uf", v)}>
                <SelectTrigger data-testid="doctor-uf-select" className="mt-1.5 border-slate-200">
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {UF_LIST.map((uf) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.uf && <p className="mt-1 text-xs text-rose-600">{errors.uf}</p>}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">
              Especialidade Médica (CFM)
            </Label>
            <Popover open={specOpen} onOpenChange={setSpecOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="doctor-specialty-select"
                  className="mt-1.5 flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition-colors hover:bg-slate-50"
                >
                  <span className={form.specialty ? "text-slate-900" : "text-slate-400"}>
                    {form.specialty || "Selecione a especialidade"}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="start">
                <Command>
                  <CommandInput data-testid="specialty-search" placeholder="Buscar especialidade..." />
                  <CommandList>
                    <CommandEmpty>Nenhuma especialidade encontrada.</CommandEmpty>
                    <CommandGroup>
                      {CFM_SPECIALTIES.map((s) => (
                        <CommandItem
                          key={s}
                          value={s}
                          onSelect={() => {
                            set("specialty", s);
                            setSpecOpen(false);
                          }}
                          className="cursor-pointer"
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              form.specialty === s ? "opacity-100 text-blue-600" : "opacity-0"
                            }`}
                          />
                          {s}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.specialty && (
              <p className="mt-1 text-xs text-rose-600">{errors.specialty}</p>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">
              Porcentagem de Repasse Individual (%)
            </Label>
            <div className="relative mt-1.5">
              <Input
                data-testid="doctor-repasse-input"
                type="number"
                min="0"
                max="100"
                value={form.repasse}
                onChange={(e) => set("repasse", e.target.value)}
                placeholder="Ex: 70"
                className="border-slate-200 pr-8 focus-visible:ring-blue-600"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                %
              </span>
            </div>
            {errors.repasse && <p className="mt-1 text-xs text-rose-600">{errors.repasse}</p>}
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            data-testid="doctor-save-btn"
            onClick={submit}
            className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            {initial ? "Salvar alterações" : "Cadastrar médico"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
